/**
 * i18n — minimal key→string dictionaries for es-MX / en-US.
 *
 * Design decisions:
 * - No library (weight budget). Plain object map.
 * - Locale detection order:
 *     1. window.MirloWidget.locale (explicit override on the host page)
 *     2. backend-provided locale (widget.extra.branding.locale, surfaced in
 *        the handshake; injected via initLocale())
 *     3. navigator.language: starts with 'en' → 'en', otherwise → 'es'
 *     4. Fallback: 'es'
 * - Agent-authored content (greeting, agent messages) is NOT translated here;
 *   it comes from the backend already in the configured agent language.
 */

export type SupportedLocale = 'es' | 'en';

type Dict = Record<string, string>;

const dicts: Record<SupportedLocale, Dict> = {
  es: {
    open_chat: 'Abrir chat',
    close_chat: 'Cerrar chat',
    dismiss_welcome: 'Cerrar mensaje de bienvenida',
    input_placeholder: 'Escribe un mensaje…',
    input_aria: 'Escribe un mensaje',
    send_message: 'Enviar mensaje',
    connecting: 'Conectando…',
    reconnecting: 'Reconectando…',
    no_connection: 'Sin conexión. Revisá tu red.',
    chat_disabled: 'Este chat fue desactivado.',
    chat_unavailable: 'El chat ya no está disponible.',
    online: 'En línea',
    online_now: 'En línea ahora',
    offline: 'Sin conexión',
    today: 'HOY',
    messages_aria: 'Mensajes del chat',
    agent_typing: 'El agente está escribiendo…',
    rate_limit: 'Demasiados mensajes. Intentá de nuevo en un momento.',
    budget_exceeded: 'Límite de uso alcanzado. Intentá más tarde.',
    generic_error: 'Ocurrió un error. Por favor intentá de nuevo.',
    attachment: 'Archivo adjunto',
    attach_file: 'Adjuntar archivo',
    drop_to_attach: 'Soltá el archivo para adjuntarlo',
    remove_attachment: 'Quitar adjunto',
    uploading_file: 'Subiendo…',
    attach_error_too_large: 'El archivo supera el límite de {max} MB.',
    attach_error_type: 'Ese tipo de archivo no está permitido.',
    attach_error_disabled: 'Este chat no acepta archivos.',
    attach_error_rate: 'Esperá un momento antes de enviar otro archivo.',
    attach_error_generic: 'No pudimos subir el archivo. Intentá de nuevo.',
    download_attachment: 'Descargar {name}',
    lets_talk: 'Hablemos.',
    lets_talk_subtitle:
      'Antes de comenzar, cuéntanos quién eres para atenderte mejor.',
    start_chat: 'Comenzar chat',
    placeholder_text: 'Escribe aquí',
    placeholder_name: '¿Cómo te llamas?',
    placeholder_email: 'tu@empresa.com',
    placeholder_phone: '+52 55 0000 0000',
    field_required: 'Este campo es requerido',
    invalid_email: 'Ingresá un email válido',
    invalid_phone: 'Ingresá un teléfono válido',
    // Conversational capture
    capture_skip: 'Prefiero no decirlo',
    corporate_email_required: 'Usá un email corporativo',
    invalid_email_domain: 'Dominio de email no permitido',
    complete_required: 'Completá los campos obligatorios para empezar',
    optional: 'opcional',
    and: 'y',
    responds_instantly: 'Responde al instante',
    online_responds_instantly: 'En línea · responde al instante',
    typing: 'escribiendo…',
    dark_mode: 'Modo oscuro',
    light_mode: 'Modo claro',
    prechat_legal: 'Al continuar aceptás recibir mensajes de {name}. Sin spam.',
    powered_by: 'Con la tecnología de',
    ai_agent: 'Agente de IA',
    human_agent: 'Agente de soporte',
    close_confirm_title: '¿Cerrar el chat?',
    close_confirm_body: 'Podés volver a abrir el chat en cualquier momento.',
    close_confirm_cancel: 'Seguir chateando',
    close_confirm_ok: 'Cerrar',
    // Availability / offline
    offline_title: 'No estamos disponibles',
    offline_default_message:
      'En este momento no hay agentes disponibles. Dejanos tu mensaje y te contactamos pronto.',
    offline_form_name: 'Nombre',
    offline_form_email: 'Email',
    offline_form_message: 'Tu mensaje',
    offline_form_submit: 'Enviar mensaje',
    offline_form_success: '¡Mensaje enviado! Te responderemos pronto.',
    offline_form_sending: 'Enviando…',
    offline_form_retry: 'Reintentar',
    offline_form_error:
      'No pudimos enviar tu mensaje. Reintentá en un momento.',
    offline_back_at: 'Regresamos a las {time}',
    offline_away: 'Fuera de horario',
    offline_agents_unavailable: 'Sin agentes disponibles',
    offline_schedule_hint: 'Horario de atención: {schedule}',
    offline_ai_available: 'Agente virtual disponible',
    offline_team_back_at: 'Equipo regresa a las {time}',
  },
  en: {
    open_chat: 'Open chat',
    close_chat: 'Close chat',
    dismiss_welcome: 'Dismiss welcome message',
    input_placeholder: 'Type a message…',
    input_aria: 'Type a message',
    send_message: 'Send message',
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    no_connection: 'No connection. Check your network.',
    chat_disabled: 'This chat has been disabled.',
    chat_unavailable: 'Chat is no longer available.',
    online: 'Online',
    online_now: 'Online now',
    offline: 'Offline',
    today: 'TODAY',
    messages_aria: 'Chat messages',
    agent_typing: 'Agent is typing…',
    rate_limit: 'Too many messages. Please try again in a moment.',
    budget_exceeded: 'Usage limit reached. Try again later.',
    generic_error: 'An error occurred. Please try again.',
    attachment: 'Attachment',
    attach_file: 'Attach file',
    drop_to_attach: 'Drop the file to attach it',
    remove_attachment: 'Remove attachment',
    uploading_file: 'Uploading…',
    attach_error_too_large: 'That file is over the {max} MB limit.',
    attach_error_type: 'That file type is not allowed.',
    attach_error_disabled: 'This chat does not accept files.',
    attach_error_rate: 'Please wait a moment before sending another file.',
    attach_error_generic: 'We could not upload the file. Please try again.',
    download_attachment: 'Download {name}',
    lets_talk: "Let's talk.",
    lets_talk_subtitle: 'Tell us a bit about you so we can help you better.',
    start_chat: 'Start chat',
    placeholder_text: 'Type here',
    placeholder_name: "What's your name?",
    placeholder_email: 'you@company.com',
    placeholder_phone: '+1 555 000 0000',
    field_required: 'This field is required',
    invalid_email: 'Enter a valid email',
    invalid_phone: 'Enter a valid phone',
    // Conversational capture
    capture_skip: "I'd rather not say",
    corporate_email_required: 'Use a corporate email',
    invalid_email_domain: 'Email domain not allowed',
    complete_required: 'Complete the required fields to start',
    optional: 'optional',
    and: 'and',
    responds_instantly: 'Responds instantly',
    online_responds_instantly: 'Online · responds instantly',
    typing: 'typing…',
    dark_mode: 'Dark mode',
    light_mode: 'Light mode',
    prechat_legal:
      'By continuing you agree to receive messages from {name}. No spam.',
    powered_by: 'Powered by',
    ai_agent: 'AI Agent',
    human_agent: 'Support agent',
    close_confirm_title: 'Close the chat?',
    close_confirm_body: 'You can reopen the chat anytime.',
    close_confirm_cancel: 'Keep chatting',
    close_confirm_ok: 'Close',
    // Availability / offline
    offline_title: "We're not available",
    offline_default_message:
      "No agents are available right now. Leave us a message and we'll get back to you soon.",
    offline_form_name: 'Name',
    offline_form_email: 'Email',
    offline_form_message: 'Your message',
    offline_form_submit: 'Send message',
    offline_form_success: "Message sent! We'll get back to you soon.",
    offline_form_sending: 'Sending…',
    offline_form_retry: 'Retry',
    offline_form_error: "We couldn't send your message. Please retry.",
    offline_back_at: 'Back at {time}',
    offline_away: 'Away',
    offline_agents_unavailable: 'No agents available',
    offline_schedule_hint: 'Business hours: {schedule}',
    offline_ai_available: 'Virtual agent available',
    offline_team_back_at: 'Team returns at {time}',
  },
};

function detectLocale(backendLocale?: SupportedLocale): SupportedLocale {
  // 1. Explicit override via window.MirloWidget.locale (host page)
  const win = window as unknown as {
    MirloWidget?: { locale?: SupportedLocale };
  };
  const override = win.MirloWidget?.locale;
  if (override === 'es' || override === 'en') return override;

  // 2. Backend-configured locale on the widget (widget.extra.branding.locale)
  if (backendLocale === 'es' || backendLocale === 'en') return backendLocale;

  // 3. navigator.language
  const lang =
    typeof navigator !== 'undefined' ? (navigator.language ?? '') : '';
  if (lang.startsWith('en')) return 'en';

  // 4. Fallback
  return 'es';
}

let _locale: SupportedLocale | null = null;

/**
 * Seed the cached locale from a backend-provided hint. Called once after the
 * handshake returns, before the widget renders, so the very first `t()` call
 * already picks the right dictionary.
 *
 * The hint only influences the result when the host page has NOT explicitly
 * set `window.MirloWidget.locale` (which always wins).
 */
export function initLocale(backendLocale?: SupportedLocale): SupportedLocale {
  _locale = detectLocale(backendLocale);
  return _locale;
}

/** Returns (and caches) the active locale. */
export function getLocale(): SupportedLocale {
  if (_locale === null) _locale = detectLocale();
  return _locale;
}

/** Reset the cached locale — used in tests to isolate locale detection. */
export function resetLocale(): void {
  _locale = null;
}

/** Translate a key using the detected locale. Supports {var} interpolation. */
export function t(key: string, vars?: Record<string, string>): string {
  const locale = getLocale();
  let result = dicts[locale][key] ?? dicts['es'][key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      result = result.replace(`{${k}}`, v);
    }
  }
  return result;
}

/** Translate using an explicit locale (useful when rendering with a known locale). */
export function tLocale(
  locale: SupportedLocale,
  key: string,
  vars?: Record<string, string>,
): string {
  let result = dicts[locale][key] ?? dicts['es'][key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      result = result.replace(`{${k}}`, v);
    }
  }
  return result;
}
